import {
  Card,
  CardContent,
  IconButton,
  makeStyles,
  Tooltip,
  Skeleton, // Import from @mui/material
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material"; // Correct import for Skeleton in v5
import Flex from "components/Flex";
import pathKeys from "constants/pathKeys";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styles from "../../styles.module.scss";
import { Doughnut } from "react-chartjs-2";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { isMobileDevice } from "utils/isMobile";
import Button from "@material-ui/core/Button";

const option_default = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    animateRotate: false,
    animateScale: false
  },
  cutout: 60,
  plugins: {
    legend: {
      position: "left",
      align: "middle",
      labels: {
        usePointStyle: true,
        generateLabels: function (chart) {
          var labels = chart.data.labels;
          var dataset = chart.data.datasets[0];
          var legend = labels.map(function (label, index) {
            return {
              datasetIndex: 0,
              fillStyle:
                dataset.backgroundColor && dataset.backgroundColor[index],
              strokeStyle: dataset.borderColor && dataset.borderColor[index],
              lineWidth: dataset.borderWidth,
              text: `${label}: ${dataset.data[index]}`
            };
          });
          return legend;
        }
      }
    }
  }
};

const options_status = {
  ...option_default
};

const options_result = {
  ...option_default,
  plugins: {
    ...option_default.plugins,
    legend: {
      ...option_default.plugins.legend,
      position: "right"
    }
  }
};

export const DashboardEmpty = () => {
  const { t } = useTranslation();
  return (
    // <Flex
    //   align="center"
    //   justify="center"
    //   style={{ width: "100%" }}
    //   className={styles.not_data}
    // >
    //   {t("update.no_data")}
    // </Flex>
    <div
      display="grid"
      align="center"
      justify="center"
      style={{ width: "100%", minWidth: "295px" }}
    >
      <Skeleton variant="text" width="100%" height={30} />
      <Skeleton variant="text" width="100%" height={30} />
      <Skeleton variant="text" width="100%" height={30} />
      <Skeleton variant="text" width="100%" height={30} />
      <Skeleton variant="text" width="100%" height={30} />
      <Skeleton variant="text" width="100%" height={30} />
      <Skeleton
        variant="text"
        width="50%"
        height={34}
        style={{ marginTop: "10px" }}
      />
    </div>
  );
};

const Chart = ({ data }) => {
  const { t, i18n } = useTranslation();

  const [openMobileDialog, setOpenMobileDialog] = useState(false);

  const handleOpenCampaign = (e) => {
    if (isMobileDevice()) {
      e.preventDefault();
      setOpenMobileDialog(true);
    }
  };

  const data_status = useMemo(() => {
    if (data) {
      const { campaign } = data;

      return {
        labels: [
          t("overall.campaign_and_message.sent"),
          t("overall.campaign_and_message.unsent"),
          t("overall.campaign_and_message.sending"),
          t("overall.campaign_and_message.cancel")
        ],
        datasets: [
          {
            data: [
              campaign?.totalCampaignDone,
              campaign?.totalCampaignPending,
              campaign?.totalCampaignRunning,
              campaign?.totalCampaignCancel
            ],
            backgroundColor: ["#05ae34", "#ffc107", "#6610f2", "#bdbdbd"],
            borderWidth: 0
          }
        ]
      };
    }
    return null;
  }, [data, i18n.language]);

  const data_result = useMemo(() => {
    if (data) {
      const { fanpage } = data;
      return {
        labels: [
          t("overall.campaign_and_message.sent_success"),
          t("overall.campaign_and_message.sent_failed"),
          t("overall.campaign_and_message.unsent")
        ],

        datasets: [
          {
            data: [
              fanpage?.totalSuccess,
              fanpage?.totalFail,
              fanpage?.totalNotSend
            ],
            backgroundColor: ["#05ae34", "#dc3545", "#666666"],
            borderWidth: 0
          }
        ]
      };
    }
    return null;
  }, [data]);

  const textCampaign = useMemo(() => {
    return {
      id: "text-center",
      beforeDatasetsDraw(chart, args, pluginOptions) {
        const { ctx } = chart;

        ctx.save();
        ctx.font = "600 14px sans-serif";
        ctx.fillStyle = "#000000de";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          `${data?.campaign?.totalCampaigns || 0}\n${t("update.campaign")}`,
          chart.getDatasetMeta(0).data[0].x,
          chart.getDatasetMeta(0).data[0].y
        );
      }
    };
  }, [data?.campaign?.totalCampaigns]);

  const textMessage = useMemo(() => {
    return {
      id: "text-message",
      beforeDatasetsDraw(chart, args, pluginOptions) {
        const { ctx } = chart;

        ctx.save();
        ctx.font = "600 14px sans-serif";
        ctx.fillStyle = "#000000de";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          `${data?.fanpage?.totalSent || 0} ${t("update.message")}`,
          chart.getDatasetMeta(0).data[0].x,
          chart.getDatasetMeta(0).data[0].y
        );
      }
    };
  }, [data?.fanpage?.totalSent]);

  return (
    <div className={styles.box}>
      <Card
        style={{
          borderRadius: "12px"
        }}
        className={cn("ml-4")}
      >
        <CardContent className="pt-4">
          <h3>{t("overall.campaign_and_message.title")}</h3>
          {!data?.campaign?.totalCampaigns ? (
            <div
              display="grid"
              align="center"
              justify="center"
              style={{ width: "100%", minWidth: "301px", height: "auto"}}
            >
              <Skeleton variant="rect" width="100%" height={163} />
              <Skeleton variant="text" width="20%" height={34} style={{marginTop: '24px'}} />
            </div>
          ) : (
            <div>
              <Flex justify="center" className="w-100">
                <Flex className={cn(styles.chart_container)} justify="center">
                  <div className={cn(styles.chart, styles.chart_status)}>
                    <Doughnut
                      data={data_status}
                      options={options_status}
                      plugins={[textCampaign]}
                    />
                  </div>
                  <div className={cn(styles.chart, styles.chart_result)}>
                    <Doughnut
                      data={data_result}
                      options={options_result}
                      plugins={[textMessage]}
                    />
                  </div>
                </Flex>
              </Flex>
              <Flex justify="center">
                <Link
                  className={cn(styles.box__action)}
                  to={`${pathKeys.CAMPAIGN}`}
                  onClick={handleOpenCampaign}
                >
                  {t("common.btn.campaign")}
                </Link>
              </Flex>
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={openMobileDialog}
        onClose={() => setOpenMobileDialog(false)}
      >
        <DialogTitle>Thông báo</DialogTitle>

        <DialogContent>
          Tính năng này chỉ khả dụng trên máy tính. Vui lòng truy cập bằng PC để tiếp tục.
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenMobileDialog(false)}
             style={{
                      backgroundColor: "#05ae34",
                      color: "white",
                      border: "1px solid #05ae34"
                    }}>
            ĐÓNG
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    
  );
};

export default Chart;
